import { decisionsToApply, ERROR_CODES, todoReasons, type ModuleKey, type Role } from '@garage/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, Outlet, useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState, type ComponentType } from 'react';
import { DemoBanner } from '../garage/components/demo/DemoBanner.js';
import { SupportModeBanner, TeamSupportBanner } from '../garage/components/support/SupportBanners.js';
import { enabledModules } from '../garage/lib/demo.js';
import { BookIcon, ChartIcon } from '../garage/ui/icons-v7.js';
import { ROLE_LABELS } from '../garage/lib/format.js';
import { useUnreadByIntervention, useUnreadCount } from '../garage/lib/inbox.js';
import { MeProvider, supportActiveMe, useActiveMe, type ActiveMe } from '../garage/lib/me.js';
import { GP, garagePath, usableMemberships } from '../garage/lib/paths.js';
import { useLeaveSupport, useLogout, useResetGarageCache } from '../garage/lib/session.js';
import { buttonClasses, Button } from '../ui/Button.js';
import {
  BuildingIcon,
  CalendarIcon,
  CarIcon,
  EntryIcon,
  ClockIcon,
  HomeIcon,
  LifebuoyIcon,
  LogOutIcon,
  MessageIcon,
  MoreIcon,
  SlidersIcon,
  UserIcon,
} from '../ui/icons.js';
import { cx } from '../ui/cx.js';
import type { MenuItem } from '../ui/Menu.js';
import { Sheet } from '../ui/Sheet.js';
import { useQuery } from '@tanstack/react-query';
import { keys } from '../garage/lib/keys.js';
import { useInputMode } from '../ui/useInputMode.js';
import { Alert, SkeletonList } from '../ui/States.js';
import { ApiError, GARAGE_MISMATCH, api, setExpectedGarage } from './api.js';
import { useAppBadgeSync } from './app-badge.js';
import { ME_QUERY_KEY, useBrand, useMe } from './auth.js';
import { useBrandDraft } from './brand-draft.js';
import { AccountMenu } from './components/AccountMenu.js';
import { BrandMark } from './components/BrandMark.js';
import { clearSessionIssue, onAccessDenied, reportApiIssue, useSessionIssue } from './session-issues.js';
import { clearLastAccess, readLastAccess, writeLastAccess } from './last-access.js';
import { GarageNotFound, NotFoundPage } from '../public/ErrorPages.js';
import { HelpIcon } from '../help/HelpLink.js';
import { HELP_HOME } from '../help/paths.js';

type NavKey = 'home' | 'agenda' | 'gestion' | 'parc' | 'rendezvous' | 'base' | 'entree' | 'messagerie' | 'compte';
type NavGroupKey = 'pilotage' | 'atelier' | 'clients' | 'garage';
/**
 * Entrées du menu, dans l'ordre, avec le droit requis et leur groupe (vague 7 : menu groupé par
 * intention). `essential` : présente dans la barre basse du téléphone ; les autres sont dans « Plus ».
 * Mêmes entrées et mêmes droits sur téléphone et ordinateur. `module` : entrée masquée quand le module
 * est désactivé dans le garage (vague 7, Compte → Modules). La liste des clients est la « Base garage »
 * filtrée sur les clients (02/10/2026).
 */
export const NAV: {
  key: NavKey;
  sub: string;
  label: string;
  group: NavGroupKey;
  essential: boolean;
  permission?: string;
  module?: ModuleKey;
  /** Entrée masquée quand ce module est **actif** (« Rendez-vous », remplacé par « Agenda », 03/10/2026). */
  withoutModule?: ModuleKey;
  /** Rôles permis (absent : tous) ; une session du support n'y a pas accès. */
  roles?: readonly Role[];
}[] = [
  { key: 'home', sub: GP.home, label: 'Accueil', group: 'pilotage', essential: true },
  // Décision du porteur (02/10/2026) : « À l'atelier » (ex-Parc) et « Base garage » (annuaire des clients et
  // des véhicules). 03/10/2026 : « Interventions » (doublon d'« À l'atelier ») devient « Rendez-vous »
  // (arrivées prévues) ; avec le module Agenda, l'entrée « Agenda » la remplace (jamais les deux). `/dossiers`
  // redirige. Barre du bas au téléphone : « Base garage » prend la place d'« Entrée » (bouton + de
  // l'Accueil ; Entrée reste dans « Plus » et dans le menu de gauche).
  { key: 'parc', sub: GP.parc, label: 'À l’atelier', group: 'atelier', essential: true },
  { key: 'rendezvous', sub: GP.rendezVous, label: 'Rendez-vous', group: 'atelier', essential: true, permission: 'intervention.view', withoutModule: 'agenda' },
  // Vague 7 (L5) : agenda, module facultatif (désactivé par défaut) : absent du menu tant qu'il est inactif.
  { key: 'agenda', sub: GP.agenda, label: 'Agenda', group: 'atelier', essential: true, permission: 'intervention.view', module: 'agenda' },
  { key: 'base', sub: GP.base, label: 'Base garage', group: 'atelier', essential: false, permission: 'intervention.view' },
  { key: 'entree', sub: GP.entree, label: 'Entrée', group: 'atelier', essential: false, permission: 'intervention.create' },
  { key: 'messagerie', sub: GP.messagerie, label: 'Messagerie', group: 'clients', essential: true, permission: 'intervention.view', module: 'central_inbox' },
  // Décision du porteur (03/10/2026) : « Gestion » (historique, activité, volume financier) remplace
  // « Chiffres » : propriétaire et associés (l'API exige `team.manage`), dans le groupe Garage du menu de
  // gauche et dans « Plus » au téléphone (jamais dans la barre du bas).
  { key: 'gestion', sub: GP.gestion, label: 'Gestion', group: 'garage', essential: false, permission: 'team.manage', roles: ['owner', 'partner'] },
  { key: 'compte', sub: GP.compte, label: 'Compte', group: 'garage', essential: false },
];

/** Groupes du menu de gauche (titres courts, dans l'ordre). */
export const NAV_GROUPS: { key: NavGroupKey; label: string }[] = [
  { key: 'pilotage', label: 'Pilotage' },
  { key: 'atelier', label: 'Atelier' },
  { key: 'clients', label: 'Clients' },
  { key: 'garage', label: 'Garage' },
];

const ICONS: Record<NavKey, ComponentType<{ size?: number; className?: string; strokeWidth?: number }>> = {
  home: HomeIcon,
  agenda: CalendarIcon,
  gestion: ChartIcon,
  parc: CarIcon,
  rendezvous: ClockIcon,
  base: BookIcon,
  entree: EntryIcon,
  messagerie: MessageIcon,
  compte: SlidersIcon,
};

/** Largeur à partir de laquelle la navigation passe en barre latérale (Tailwind `lg`). */
export const DESKTOP_QUERY = '(min-width: 1024px)';

function matches(query: string): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(query).matches
  );
}

/** Suit une requête média. Sans `matchMedia` (anciens navigateurs, tests), on reste en mode téléphone. */
export function useMediaQuery(query: string): boolean {
  const [value, setValue] = useState(() => matches(query));
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const onChange = () => setValue(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return value;
}

// Le lien actif reçoit `aria-current="page"` du routeur ; le style suit cet attribut.
const EXACT = { exact: true } as const;

/** Écran plein, centré (chargement, garage introuvable, garage suspendu). */
function CenteredScreen({ children }: { children: React.ReactNode }) {
  return (
    <main className="grid min-h-dvh place-items-center bg-canvas px-[var(--page-x)] py-10">
      <div className="w-full max-w-md">{children}</div>
    </main>
  );
}

// Pages d'erreur publiques (garage introuvable, page introuvable) : portail global de la plateforme.
export { GarageNotFound, NotFoundPage };

/** Garage suspendu (appartenance suspendue ou code `garage_suspended`) : message clair, aucune donnée. */
function GarageSuspended({ message, canChoose }: { message: string; canChoose: boolean }) {
  useBrand(null, 'Accès suspendu');
  const logout = useLogout();
  return (
    <CenteredScreen>
      <h1 className="text-2xl font-semibold tracking-[-0.02em] text-ink">Accès suspendu</h1>
      <Alert tone="warning" className="mt-4">
        {message}
      </Alert>
      <div className="mt-6 space-y-2">
        {canChoose ? (
          <Link to="/choose-garage" className={buttonClasses({ variant: 'secondary', size: 'lg', block: true })}>
            Choisir un autre garage
          </Link>
        ) : null}
        <Button variant="quiet" size="lg" block onClick={() => void logout()}>
          Se déconnecter
        </Button>
      </div>
    </CenteredScreen>
  );
}

/**
 * C1 : accès retiré à un membre pendant qu'il travaillait (ou avant un rechargement de l'onglet).
 * Seul l'onglet qui avait accès à ce garage le sait (mémoire de l'onglet) : aucune énumération.
 */
export function AccessRemoved({ canChoose }: { canChoose: boolean }) {
  useBrand(null, 'Accès retiré');
  const logout = useLogout();
  return (
    <CenteredScreen>
      <h1 className="text-2xl font-semibold tracking-[-0.02em] text-ink">Accès retiré</h1>
      <p className="mt-2 text-ink-2">
        Votre accès à ce garage a été retiré. Pour le retrouver, demandez au responsable du garage de vous inviter de nouveau.
      </p>
      <div className="mt-6 space-y-2">
        {canChoose ? (
          <Link to="/choose-garage" onClick={clearLastAccess} className={buttonClasses({ variant: 'secondary', size: 'lg', block: true })}>
            Choisir un autre garage
          </Link>
        ) : null}
        <Button
          variant="quiet"
          size="lg"
          block
          onClick={() => {
            clearLastAccess();
            void logout();
          }}
        >
          Se déconnecter
        </Button>
      </div>
    </CenteredScreen>
  );
}

/**
 * S35 : la session est passée sur un autre garage dans un autre onglet (409 `garage_mismatch`).
 * Rien n'a été enregistré ; l'utilisateur recharge le garage de l'adresse avant de continuer.
 */
export function GarageChanged({ slug, garageName }: { slug: string; garageName: string | null }) {
  useBrand(null, 'Garage actif changé');
  const reset = useResetGarageCache();
  const [state, setState] = useState<'idle' | 'busy' | 'error'>('idle');
  const reload = () => {
    setState('busy');
    api('auth.switchGarage', { body: { slug } })
      .then((next) => {
        reset(next);
        clearSessionIssue();
      })
      .catch(() => setState('error'));
  };
  const name = garageName ?? 'ce garage';
  return (
    <CenteredScreen>
      <h1 className="text-2xl font-semibold tracking-[-0.02em] text-ink">Le garage actif a changé dans un autre onglet</h1>
      <p className="mt-2 text-ink-2">
        Un autre onglet a ouvert un autre garage. Votre dernière action n’a pas été enregistrée : rechargez {name}, puis recommencez-la si
        besoin.
      </p>
      {state === 'error' ? (
        <Alert tone="danger" className="mt-4">
          Rechargement impossible. Vérifiez le réseau et réessayez.
        </Alert>
      ) : null}
      <Button size="lg" block className="mt-6" loading={state === 'busy'} onClick={reload}>
        Recharger
      </Button>
    </CenteredScreen>
  );
}

const SUPPORT_ENDED_MESSAGE = 'L’accès support a pris fin.';

/**
 * A1 / S30 : l'accès support a pris fin (retrait par le garage, échéance, fin). L'API refuse tout
 * (403 `support_access_ended`) : on quitte le mode support (coque et bandeau retirés). « Retour à la
 * console », et « Aller à mon garage » si l'administrateur est aussi membre d'un garage.
 */
export function SupportEnded({ memberships }: { memberships: { garageSlug: string; garageName: string }[] }) {
  useBrand(null, 'Accès retiré ou terminé');
  const navigate = useNavigate();
  const reset = useResetGarageCache();
  const [state, setState] = useState<'idle' | 'busy' | 'error'>('idle');
  const leave = () => {
    clearLastAccess();
    setExpectedGarage(null);
  };
  const own = memberships[0];
  const goGarage = () => {
    if (!own) return;
    if (memberships.length > 1) {
      leave();
      void navigate({ to: '/choose-garage' });
      return;
    }
    setState('busy');
    api('auth.switchGarage', { body: { slug: own.garageSlug } })
      .then((next) => {
        leave();
        reset(next);
        void navigate({ to: garagePath(own.garageSlug) });
      })
      .catch(() => setState('error'));
  };
  return (
    <CenteredScreen>
      <h1 className="text-2xl font-semibold tracking-[-0.02em] text-ink">Accès retiré ou terminé</h1>
      <p className="mt-2 text-ink-2">L’accès support à ce garage a pris fin (retiré par le garage ou arrivé à échéance). Plus aucune donnée du garage n’est accessible.</p>
      {state === 'error' ? (
        <Alert tone="danger" className="mt-4">
          Changement de garage impossible. Vérifiez le réseau et réessayez.
        </Alert>
      ) : null}
      <div className="mt-6 space-y-2">
        <Button
          size="lg"
          block
          onClick={() => {
            leave();
            void navigate({ to: '/admin' });
          }}
        >
          Retour à la console
        </Button>
        {own ? (
          <Button variant="secondary" size="lg" block loading={state === 'busy'} onClick={goGarage}>
            {memberships.length > 1 ? 'Aller à un de mes garages' : 'Aller à mon garage'}
          </Button>
        ) : null}
      </div>
    </CenteredScreen>
  );
}

const SUSPENDED_MESSAGE = 'L’accès à ce garage est suspendu. Contactez l’éditeur du service.';

/**
 * Espace garage `/g/:slug/…` (vague 5). Le slug sert à naviguer : si le garage actif de la session
 * (ou de la session support) n'est pas celui de l'adresse, l'interface appelle
 * `auth.switchGarage({ slug })` ; le serveur ne lit jamais le slug pour une route `garage`.
 * 404 → « Garage introuvable » ; appartenance suspendue ou `garage_suspended` → écran dédié ;
 * `support_access_ended` → retour à la console.
 */
export function GarageSpace() {
  const { slug } = useParams({ strict: false }) as { slug?: string };
  const me = useMe();
  const issue = useSessionIssue();
  const reset = useResetGarageCache();
  const qc = useQueryClient();
  const [switchState, setSwitchState] = useState<{ slug: string; state: 'notfound' | 'error' } | null>(null);
  const data = me.data;

  const ctx = useMemo<ActiveMe | null>(() => {
    if (!data || !slug) return null;
    if (data.support?.garageSlug === slug) return supportActiveMe(data);
    if (data.active?.garageSlug === slug) return { ...data, active: data.active, supportSession: null };
    return null;
  }, [data, slug]);
  const suspendedHere = !!data?.memberships.some((m) => m.garageSlug === slug && m.status === 'suspended');

  // Ce que l'onglet savait de ce garage (accès de membre ou session support), pour reconnaître un
  // accès perdu (C1, A1) au lieu d'afficher « Garage introuvable ».
  const ctxSupport = ctx ? !!ctx.supportSession : null;
  useEffect(() => {
    if (slug && ctxSupport !== null) writeLastAccess({ slug, support: ctxSupport });
  }, [slug, ctxSupport]);
  const last = slug ? readLastAccess() : null;
  const knownHere = !!data && !!slug && !ctx && last?.slug === slug;
  const supportLost = knownHere && !!last?.support;
  const memberLost = knownHere && !last?.support && !data?.memberships.some((m) => m.garageSlug === slug);
  const mismatch = issue?.code === GARAGE_MISMATCH;
  const needSwitch = !!data && !!slug && !ctx && !suspendedHere && !supportLost && !memberLost && !mismatch && issue?.code !== ERROR_CODES.supportAccessEnded;

  // S35 : garage attendu, envoyé en en-tête sur chaque requête de l'espace garage. Posé pendant le
  // rendu (valeur de module idempotente) : les requêtes des écrans enfants partent dans leurs effets,
  // qui s'exécutent avant ceux de ce composant.
  setExpectedGarage(ctx && slug ? slug : null);
  useEffect(() => () => setExpectedGarage(null), []);

  // Un refus de l'API (401, 403) peut signifier la fin de l'accès lui-même : on relit la session.
  const lastProbe = useRef(0);
  useEffect(
    () =>
      onAccessDenied(() => {
        const now = Date.now();
        if (now - lastProbe.current < 2000) return;
        lastProbe.current = now;
        void qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
      }),
    [qc],
  );

  useEffect(() => {
    if (!needSwitch || !slug) return;
    let cancelled = false;
    api('auth.switchGarage', { body: { slug } })
      .then((next) => {
        if (cancelled) return;
        reset(next);
        setSwitchState(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setSwitchState({ slug, state: err instanceof ApiError && err.status === 404 ? 'notfound' : 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [needSwitch, slug, reset]);

  // A1 / S30 : accès support terminé (échéance, retrait ; code `support_access_ended`, ou l'ancien
  // `forbidden` suivi d'une session relue sans accès support) : on quitte le mode support (coque et
  // bandeau retirés) ; la console affichera aussi « L'accès support a pris fin ».
  const supportEnded = issue?.code === ERROR_CODES.supportAccessEnded || supportLost;
  useEffect(() => {
    if (!supportEnded) return;
    if (issue?.code !== ERROR_CODES.supportAccessEnded) reportApiIssue(ERROR_CODES.supportAccessEnded, SUPPORT_ENDED_MESSAGE);
    void qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
  }, [supportEnded, issue?.code, qc]);

  // S35 : 409 `garage_mismatch` : la requête n'est pas rejouée ; on relit la session (bascule
  // automatique suspendue) et l'écran « Le garage actif a changé dans un autre onglet » propose « Recharger ».
  useEffect(() => {
    if (mismatch) void qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
  }, [mismatch, qc]);

  if (me.isPending) {
    return (
      <CenteredScreen>
        <SkeletonList rows={3} />
      </CenteredScreen>
    );
  }
  if (me.isError) {
    return (
      <CenteredScreen>
        <Alert tone="danger" action={<Button variant="secondary" onClick={() => void me.refetch()}>Réessayer</Button>}>
          Le serveur ne répond pas correctement. Réessayez.
        </Alert>
      </CenteredScreen>
    );
  }
  if (!data) return <Navigate to="/login" replace />;
  const others = usableMemberships(data).filter((m) => m.garageSlug !== slug);
  if (supportEnded) return <SupportEnded memberships={usableMemberships(data)} />;
  if (memberLost) return <AccessRemoved canChoose={others.length > 0 || data.isPlatformAdmin} />;
  if (mismatch && slug) {
    const garageName = ctx?.active.garageName ?? data.memberships.find((m) => m.garageSlug === slug)?.garageName ?? null;
    return <GarageChanged slug={slug} garageName={garageName} />;
  }
  if (issue?.code === ERROR_CODES.garageSuspended) return <GarageSuspended message={issue.message || SUSPENDED_MESSAGE} canChoose={others.length > 0} />;
  if (suspendedHere) return <GarageSuspended message={SUSPENDED_MESSAGE} canChoose={others.length > 0} />;
  if (switchState?.slug === slug && switchState?.state === 'notfound') return <GarageNotFound />;
  if (switchState?.slug === slug && switchState?.state === 'error') {
    return (
      <CenteredScreen>
        <Alert tone="danger">Connexion impossible. Vérifiez le réseau et réessayez.</Alert>
      </CenteredScreen>
    );
  }
  if (!ctx) {
    return (
      <CenteredScreen>
        <SkeletonList rows={3} />
      </CenteredScreen>
    );
  }
  return (
    <MeProvider me={ctx}>
      <GarageShell />
    </MeProvider>
  );
}

/**
 * Compteur d'une entrée du menu (messages non lus, décisions reçues), aux couleurs de la barre : accent
 * (2e couleur en modes affirmé et immersif), inversé quand l'entrée est active. Décoratif : le nombre
 * est dit par le nom ou la description du lien.
 */
function NavCount({ count, compact }: { count: number; compact?: boolean }) {
  if (count <= 0) return null;
  return (
    <span
      aria-hidden
      className={
        compact
          ? 'absolute -top-1 left-[calc(50%+4px)] grid h-[18px] min-w-[18px] place-items-center rounded-full bg-chrome-badge px-1 text-[0.6875rem] leading-none font-semibold text-on-chrome-badge tabular-nums ring-2 ring-chrome'
          : 'ml-auto grid h-[18px] min-w-[18px] place-items-center rounded-full bg-chrome-badge px-1.5 text-2xs leading-none font-medium text-on-chrome-badge tabular-nums'
      }
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

type NavCounter = { count: number; text: string };

/**
 * Compteurs du menu (vague 7) : messages non lus (Messagerie) et, depuis le 03/10/2026, véhicules « À
 * traiter » sur « À l'atelier » (même règle que la puce : `todoReasons`, non-lus par dossier compris ; ex-
 * compteur des décisions reçues d'« Interventions »), sur la liste des présents (`interventions.list`,
 * même clé de cache que l'Accueil et « À l'atelier »). Désactivés sans `intervention.view` et en session support.
 */
function useNavCounters(): Partial<Record<NavKey, NavCounter>> {
  const me = useActiveMe();
  const unreadQuery = useUnreadCount();
  const unread = unreadQuery.data?.unread ?? 0;
  const enabled = me.active.permissions.includes('intervention.view') && !me.supportSession;
  const home = useQuery({
    queryKey: keys.home,
    queryFn: () => api('home.get'),
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  // Même liste que la puce « À traiter » et « À faire maintenant » de l'Accueil (même clé de cache).
  const present = useQuery({
    queryKey: keys.priority,
    queryFn: () => api('interventions.list', { query: { scope: 'present', sort: 'priority' } }),
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  const unreadOf = useUnreadByIntervention();
  const todo = (present.data ?? []).filter((i) => todoReasons(i, unreadOf(i.id)).length > 0).length;
  useAppBadgeSync({
    active: enabled && !me.demo,
    unread: unreadQuery.data?.unread,
    decisions: home.data?.parc ? decisionsToApply(home.data.parc.flatMap((g) => g.interventions)) : undefined,
  });
  const plural = (n: number, one: string, many: string) => (n > 1 ? many : one);
  return {
    messagerie: { count: unread, text: `${unread} non ${plural(unread, 'lu', 'lus')}` },
    parc: { count: todo, text: `${todo} ${plural(todo, 'véhicule à traiter', 'véhicules à traiter')}` },
  };
}

/** Nom accessible d'une entrée : la Messagerie dit ses non-lus dans son nom (« Messagerie, 2 non lus »). */
function navName(item: (typeof NAV)[number], counter: NavCounter | undefined): string | undefined {
  return item.key === 'messagerie' && counter && counter.count > 0 ? `${item.label}, ${counter.text}` : undefined;
}

/** Les autres compteurs (véhicules à traiter) décrivent le lien sans changer son nom (« À l'atelier »). */
function described(item: (typeof NAV)[number], counter: NavCounter | undefined): boolean {
  return item.key !== 'messagerie' && !!counter && counter.count > 0;
}

/** Entrées du menu du compte (barre latérale, en-tête mobile) : profil, garage, déconnexion, support. */
function useAccountMenuItems(): MenuItem[] {
  const me = useActiveMe();
  const navigate = useNavigate();
  const logout = useLogout();
  const leaveSupport = useLeaveSupport();
  const support = !!me.supportSession;
  const items: MenuItem[] = [
    {
      key: 'profil',
      label: 'Mon profil',
      icon: <UserIcon size={18} />,
      onSelect: () => void navigate({ to: garagePath(me.active.garageSlug, GP.compteSection('profil')) }),
    },
  ];
  if (!support && !me.demo && usableMemberships(me).length > 1) {
    items.push({ key: 'garage', label: 'Changer de garage', icon: <BuildingIcon size={18} />, onSelect: () => void navigate({ to: '/choose-garage' }) });
  }
  if (support) {
    // L'accès reste ouvert jusqu'à son échéance ; « Rendre l'accès » est dans le bandeau support.
    // Sort du mode support sans rendre l'accès (`platform.support.leave`) ; « Rendre l'accès » est dans le bandeau.
    items.push({ key: 'support', label: 'Quitter le mode support', icon: <LifebuoyIcon size={18} />, onSelect: () => leaveSupport.mutate() });
  }
  // Vague 7 : une session de démonstration se quitte (aucun compte à déconnecter).
  items.push({ key: 'logout', label: me.demo ? 'Quitter la démonstration' : 'Se déconnecter', icon: <LogOutIcon size={18} />, onSelect: () => void logout() });
  return items;
}

/**
 * Gabarit de l'espace garage (habillage : docs/DESIGN.md, « Présence de la marque »). Téléphone tenu
 * d'une main d'abord : en dessous de 1024 px, en-tête aux couleurs du garage (icône, nom, menu du
 * compte), contenu sur une colonne et navigation basse au pouce. À partir de 1024 px, barre latérale
 * fixe à gauche (icône et nom du garage en haut, menu du compte en bas) et contenu centré. Une seule
 * navigation est rendue à la fois. Les couleurs suivent le mode de marque du garage (discret, affirmé,
 * immersif), y compris l'aperçu en direct de Compte → Marque (`useBrandDraft`).
 */
function GarageShell() {
  const me = useActiveMe();
  const desktop = useMediaQuery(DESKTOP_QUERY);
  // Clavier ouvert (téléphone) : la navigation basse laisse la place au champ et aux actions.
  const { typing } = useInputMode();
  const saved = me.active.branding;
  const draft = useBrandDraft();
  const brand = useMemo(() => (draft ? { ...saved, ...draft } : saved), [saved, draft]);
  useBrand(brand);
  const name = brand.displayName;
  const counters = useNavCounters();
  const menuItems = useAccountMenuItems();
  const [moreOpen, setMoreOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Le menu reflète les droits ; l'API reste seule juge (requirePermission).
  const modules = enabledModules(me);
  const items = NAV.filter(
    (item) =>
      (!item.permission || me.active.permissions.includes(item.permission as never)) &&
      (!item.module || modules.has(item.module)) &&
      (!item.withoutModule || !modules.has(item.withoutModule)) &&
      (!item.roles || (!me.supportSession && item.roles.includes(me.active.role))),
  );
  const href = (sub: string) => garagePath(me.active.garageSlug, sub);
  const banners = me.supportSession ? <SupportModeBanner /> : me.demo ? <DemoBanner /> : <TeamSupportBanner />;
  const roleLabel = me.supportSession ? 'Support' : ROLE_LABELS[me.active.role];
  const account = {
    name: me.user.name,
    email: me.user.email,
    detail: roleLabel,
    tone: me.supportSession ? ('author-support' as const) : undefined,
    items: menuItems,
  };

  if (desktop) {
    return (
      <div className="flex min-h-screen bg-canvas">
        <aside className="sticky top-0 flex h-screen w-[var(--sidebar-w)] shrink-0 flex-col border-r border-chrome-line bg-chrome text-on-chrome">
          <div className="flex h-14 shrink-0 items-center gap-2.5 px-3.5">
            <BrandMark name={name} logoUrl={null} iconUrl={brand.iconUrl} size="sm" />
            <span className="min-w-0 text-base leading-tight font-semibold tracking-[-0.01em] text-on-chrome">{name}</span>
          </div>
          <nav aria-label="Navigation principale" className="flex-1 overflow-y-auto px-2.5 pt-1 pb-4">
            <NavGroups items={items} href={href} counters={counters} variant="sidebar" />
          </nav>
          <div className="border-t border-chrome-line p-2">
            <AccountMenu variant="sidebar" {...account} />
          </div>
        </aside>
        <main className="min-w-0 flex-1">
          {banners}
          <div className="mx-auto w-full max-w-[1280px] px-[var(--page-x)] pt-6 pb-12">
            <Outlet />
          </div>
        </main>
      </div>
    );
  }

  // Téléphone : les entrées essentielles dans la barre basse, toutes les rubriques dans « Plus ».
  const bar = items.filter((item) => item.essential);
  const more = items.filter((item) => !item.essential);
  const moreActive = more.some((item) => pathname.startsWith(href(item.sub)));
  const moreCount = more.reduce((n, item) => n + (counters[item.key]?.count ?? 0), 0);

  return (
    <div className={`flex min-h-dvh flex-col bg-canvas ${typing ? '[--bottom-nav-h:0px]' : '[--bottom-nav-h:64px]'}`}>
      <header className="border-b-2 border-masthead-line bg-masthead pt-[env(safe-area-inset-top)] text-on-masthead">
        <div className="mx-auto flex h-14 max-w-3xl items-center gap-3 pr-[calc(var(--page-x)-6px)] pl-[var(--page-x)]">
          <BrandMark name={name} logoUrl={null} iconUrl={brand.iconUrl} size="sm" />
          <span className="min-w-0 flex-1 truncate text-base font-semibold tracking-[-0.01em]">{name}</span>
          <AccountMenu variant="masthead" {...account} />
        </div>
      </header>
      {banners}
      {/* Pendant la saisie, de la place sous le contenu : le dernier champ peut remonter au-dessus du clavier. */}
      <main className={`mx-auto w-full max-w-3xl flex-1 px-[var(--page-x)] pt-4 ${typing ? 'pb-[60dvh]' : 'pb-8'}`}>
        <Outlet />
      </main>
      <nav
        aria-label="Navigation principale"
        hidden={typing}
        className="sticky bottom-0 z-20 border-t border-chrome-line bg-chrome/95 pb-[var(--safe-bottom)] text-on-chrome backdrop-blur-md"
      >
        <div className="mx-auto grid max-w-3xl" style={{ gridTemplateColumns: `repeat(${bar.length + (more.length > 0 ? 1 : 0)}, minmax(0, 1fr))` }}>
          {bar.map((item) => {
            const Icon = ICONS[item.key];
            const counter = counters[item.key];
            return (
              <Link
                key={item.key}
                to={href(item.sub)}
                aria-label={navName(item, counter)}
                aria-describedby={described(item, counter) ? `nav-bar-${item.key}-desc` : undefined}
                activeOptions={item.key === 'home' ? EXACT : undefined}
                className={BOTTOM_ITEM}
              >
                <span className={BOTTOM_PILL}>
                  <Icon size={22} />
                  <NavCount count={counter?.count ?? 0} compact />
                </span>
                {/* « Base garage » est le libellé le plus long : jamais de débordement à 360 px. */}
                <span className="max-w-full truncate tracking-[-0.01em]">{item.label}</span>
                {described(item, counter) ? <span id={`nav-bar-${item.key}-desc`} hidden>{counter?.text}</span> : null}
              </Link>
            );
          })}
          {more.length > 0 ? (
            <button
              type="button"
              aria-haspopup="dialog"
              aria-expanded={moreOpen}
              aria-current={moreActive ? 'page' : undefined}
              onClick={() => setMoreOpen(true)}
              className={BOTTOM_ITEM}
            >
              <span className={BOTTOM_PILL}>
                <MoreIcon size={22} />
                <NavCount count={moreCount} compact />
              </span>
              Plus
            </button>
          ) : null}
        </div>
      </nav>
      {/* Rendue seulement ouverte : ses liens ne doublent pas ceux de la barre pour les lecteurs d’écran. */}
      {moreOpen ? (
        <Sheet open onClose={() => setMoreOpen(false)} title="Toutes les rubriques">
          <nav aria-label="Toutes les rubriques" className="-mx-2">
            <NavGroups items={items} href={href} counters={counters} variant="sheet" onNavigate={() => setMoreOpen(false)} />
          </nav>
        </Sheet>
      ) : null}
    </div>
  );
}

/** Entrée de la barre basse : icône dans une pastille (active : fond de l'élément actif), libellé dessous. */
const BOTTOM_ITEM =
  'group flex min-h-16 flex-col items-center justify-center gap-1 px-0.5 pt-1.5 pb-1 text-[0.6875rem] leading-none text-on-chrome-3 focus-visible:outline-chrome-focus focus-visible:-outline-offset-4 aria-[current=page]:font-medium aria-[current=page]:text-on-chrome';
const BOTTOM_PILL =
  'relative grid h-7 w-12 place-items-center rounded-full transition-colors duration-150 group-aria-[current=page]:bg-chrome-active-soft group-aria-[current=page]:text-chrome-active-soft-icon';

/**
 * Menu groupé par intention (vague 7) : Pilotage, Atelier, Clients, Garage. Un groupe sans entrée
 * autorisée n'est pas affiché. Barre latérale (ordinateur) ou feuille « Plus » (téléphone).
 */
function NavGroups({
  items,
  href,
  counters,
  variant,
  onNavigate,
}: {
  items: typeof NAV;
  href: (sub: string) => string;
  counters: Partial<Record<NavKey, NavCounter>>;
  variant: 'sidebar' | 'sheet';
  onNavigate?: () => void;
}) {
  const sidebar = variant === 'sidebar';
  return (
    <>
      {NAV_GROUPS.map((group) => {
        const entries = items.filter((item) => item.group === group.key);
        if (entries.length === 0) return null;
        const titleId = `nav-${variant}-${group.key}`;
        return (
          <div key={group.key} role="group" aria-labelledby={titleId} className="mt-4 first:mt-1">
            <p id={titleId} className={cx('px-2.5 pb-1 text-2xs font-medium tracking-[0.06em] uppercase', sidebar ? 'text-on-chrome-3' : 'text-ink-3')}>
              {group.label}
            </p>
            <ul className="space-y-px">
              {entries.map((item) => {
                const Icon = ICONS[item.key];
                const counter = counters[item.key];
                return (
                  <li key={item.key}>
                    <Link
                      to={href(item.sub)}
                      onClick={onNavigate}
                      aria-label={navName(item, counter)}
                      aria-describedby={described(item, counter) ? `${titleId}-${item.key}-desc` : undefined}
                      activeOptions={item.key === 'home' ? EXACT : undefined}
                      className={
                        sidebar
                          ? "group relative flex min-h-(--ctl-row) items-center gap-2.5 rounded-md px-2.5 text-base text-on-chrome-2 transition-colors duration-150 before:absolute before:inset-y-1.5 before:-left-2.5 before:w-[3px] before:rounded-r-full before:bg-[var(--chrome-indicator)] before:opacity-0 before:content-[''] hover:bg-chrome-hover hover:text-on-chrome focus-visible:outline-chrome-focus aria-[current=page]:bg-chrome-active-soft aria-[current=page]:font-medium aria-[current=page]:text-chrome-active-soft-ink aria-[current=page]:before:opacity-100"
                          : 'group flex min-h-(--ctl-sm) items-center gap-3 rounded-xl px-3 text-base text-ink-2 hover:bg-sunken aria-[current=page]:bg-brand-soft aria-[current=page]:font-medium aria-[current=page]:text-ink'
                      }
                    >
                      <Icon
                        size={sidebar ? 17 : 19}
                        className={cx(
                          'shrink-0 transition-colors',
                          sidebar
                            ? 'text-on-chrome-3 group-hover:text-on-chrome-2 group-aria-[current=page]:text-chrome-active-soft-icon'
                            : 'text-ink-3 group-aria-[current=page]:text-brand-strong',
                        )}
                      />
                      <span className="min-w-0 flex-1 truncate">{item.label}</span>
                      {described(item, counter) ? <span id={`${titleId}-${item.key}-desc`} hidden>{counter?.text}</span> : null}
                      {sidebar ? (
                        <NavCount count={counter?.count ?? 0} />
                      ) : counter && counter.count > 0 ? (
                        <span aria-hidden className="grid h-5 min-w-5 place-items-center rounded-full bg-brand px-1.5 text-xs font-medium text-on-brand tabular-nums">
                          {counter.count > 99 ? '99+' : counter.count}
                        </span>
                      ) : null}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
      {/* 08/10/2026 : centre d'aide de l'équipe (public, hors de l'espace du garage), sous les groupes. */}
      <ul className="mt-4 border-t border-chrome-line pt-2">
        <li>
          <Link
            to={HELP_HOME.team}
            onClick={onNavigate}
            className={
              sidebar
                ? 'group flex min-h-(--ctl-row) items-center gap-2.5 rounded-md px-2.5 text-base text-on-chrome-2 transition-colors duration-150 hover:bg-chrome-hover hover:text-on-chrome focus-visible:outline-chrome-focus'
                : 'group flex min-h-(--ctl-sm) items-center gap-3 rounded-xl px-3 text-base text-ink-2 hover:bg-sunken'
            }
          >
            <HelpIcon size={sidebar ? 17 : 19} className={cx('shrink-0', sidebar ? 'text-on-chrome-3 group-hover:text-on-chrome-2' : 'text-ink-3')} />
            <span className="min-w-0 flex-1 truncate">Aide</span>
          </Link>
        </li>
      </ul>
    </>
  );
}

export function PortalLayout() {
  return (
    <div className="min-h-dvh bg-canvas">
      <Outlet />
    </div>
  );
}

export function EmptyPage({ title, lot }: { title: string; lot: string }) {
  return (
    <section>
      <h1 className="text-2xl font-semibold tracking-[-0.02em] text-ink">{title}</h1>
      <p className="mt-2 text-sm text-ink-3">Page à construire ({lot}).</p>
    </section>
  );
}
